/**
 * Identify what physical structure a turn uses.
 *
 * Mapbox Directions says only "uturn". That is not enough in Thailand: an
 * ordinary median opening, a turn beneath a flyover and a dedicated U-turn
 * bridge require different lane preparation. We first honour explicit route
 * text/intersection metadata, then do one best-effort OSM structure lookup for
 * all actionable turns in the candidate routes. A failed lookup stays `unknown`; it
 * must never turn into a confident but invented bridge instruction.
 */

import type { LngLat, Route, RouteStep } from '../core/types';
import { bearing, bearingDelta, distance, nearestPointOnSegment } from '../core/geo';

export type UturnStructure = 'road' | 'under-bridge' | 'bridge' | 'unknown';
export type ManeuverStructure = UturnStructure;

interface ManeuverSite {
  step: RouteStep;
  point: LngLat;
  paths: LngLat[][];
  exitPath?: LngLat[];
}

interface OverpassWay {
  type: 'way';
  id: number;
  geometry?: Array<{ lat: number; lon: number }>;
  tags?: Record<string, string>;
}

const STRUCTURE = new WeakMap<RouteStep, UturnStructure>();
const OVERPASS_URLS = [
  'https://overpass-api.de/api/interpreter',
  'https://overpass.kumi.systems/api/interpreter',
  'https://overpass.private.coffee/api/interpreter',
];

/** Close enough that the structured OSM way is the route itself. */
const ON_ROUTE_M = 18;
/** A bridge this close to the turning point is the structure above the turn. */
const AT_JUNCTION_M = 45;
/** Search radius around each U-turn. */
const LOOKUP_M = 65;

const UNDER_WORDS =
  /\b(?:u[- ]?turn|turn(?:ing)? around)\s+(?:beneath|under)\s+(?:the\s+)?(?:bridge|flyover|overpass)\b|\b(?:beneath|under)\s+(?:the\s+)?(?:bridge|flyover|overpass)\b|กลับรถใต้สะพาน|ลอดใต้สะพาน|ทางลอดกลับรถ|อุโมงค์กลับรถ/iu;
const BRIDGE_WORDS =
  /\b(?:u[- ]?turn|turnaround|turnback)\s+(?:bridge|flyover|overpass)\b|\b(?:bridge|flyover|overpass)\s+(?:u[- ]?turn|turnaround|turnback)\b|สะพานกลับรถ|ทางยกระดับกลับรถ/iu;
const ROAD_WORDS =
  /\b(?:u[- ]?turn|turnaround|turnback)\s+(?:on|across)\s+(?:the\s+)?road\b|กลับรถบนถนน/iu;

/** Maximum connector length between the two turns of one mapped U-turn. */
const COMPOUND_UTURN_LINK_M = 400;
/** Consecutive Mapbox steps share this endpoint; looser pairs are separate turns. */
const COMPOUND_UTURN_JOIN_M = 20;

function hardTurnSide(step: RouteStep | null | undefined): 'left' | 'right' | null {
  const type = (step?.maneuver.type ?? '').toLowerCase();
  if (type !== 'turn' && type !== 'end of road') return null;
  const modifier = (step?.maneuver.modifier ?? '').toLowerCase();
  if (modifier === 'left' || modifier === 'sharp left') return 'left';
  if (modifier === 'right' || modifier === 'sharp right') return 'right';
  return null;
}

function normalizedRoadName(step: RouteStep | null | undefined): string {
  const name = (step?.name ?? '').normalize('NFKC').trim().toLowerCase().replace(/\s+/g, ' ');
  return /^(?:unnamed|unnamed road|unknown road|road|street|service road|ถนน)$/u.test(name)
    ? ''
    : name;
}

function directRoadRefs(step: RouteStep): string[] {
  return (step.ref ?? '')
    .split(/[;,/]/)
    .map((ref) => ref.trim().toUpperCase())
    .filter(Boolean);
}

/** Positive identity only: an unknown road must not become an invented U-turn. */
function returnsToApproachRoad(approach: RouteStep, exit: RouteStep): boolean {
  // Use the road's own ref only. Banner shields describe an upcoming road and
  // can make two ordinary turns look as though they returned to the approach.
  const beforeRefs = directRoadRefs(approach);
  const afterRefs = directRoadRefs(exit);
  if (beforeRefs.some((ref) => afterRefs.includes(ref))) return true;

  const beforeName = normalizedRoadName(approach);
  const afterName = normalizedRoadName(exit);
  return !!beforeName && beforeName === afterName;
}

/**
 * Some Thai underpass U-turns are mapped as two same-side 90-degree turns
 * joined by a short road. Presenting only the first one as "turn right" hides
 * the actual manoeuvre and is exactly what the photographed Rama 6 junction
 * did. Fold the pair only when the final heading is genuinely reversed.
 */
export function compoundUturnSide(
  first: RouteStep | null | undefined,
  second: RouteStep | null | undefined,
  approach: RouteStep | null | undefined,
): 'left' | 'right' | null {
  const side = hardTurnSide(first);
  if (!side || hardTurnSide(second) !== side || !first || !second || !approach) return null;
  if (!Number.isFinite(first.distance) || first.distance > COMPOUND_UTURN_LINK_M) return null;

  /*
   * The UI supplies the next SIGNIFICANT maneuver, which may skip raw route
   * steps. `first.distance` only reaches the next raw step, so it cannot prove
   * that `second` is the other half of this turn. Consecutive Mapbox steps
   * meet at the first geometry's endpoint; reject the pair when they do not.
   */
  const firstEnd = first.geometry?.coordinates.at(-1);
  if (!firstEnd || distance(firstEnd, second.maneuver.location) > COMPOUND_UTURN_JOIN_M) {
    return null;
  }

  // Two ordinary turns around a short city block can also reverse the heading.
  // A real compound U-turn exits onto the road it approached on; fail closed
  // when Mapbox supplies no positive road identity rather than inventing one.
  if (!returnsToApproachRoad(approach, second)) return null;

  const before = first.maneuver.bearing_before;
  const after = second.maneuver.bearing_after;
  if (!Number.isFinite(before) || !Number.isFinite(after)) return null;
  return Math.abs(bearingDelta(before, after)) >= 135 ? side : null;
}

export function isUturnStep(step: RouteStep | null | undefined): step is RouteStep {
  if (!step) return false;
  if ((step.maneuver.modifier ?? '').toLowerCase() === 'uturn') return true;
  return !!step.bannerInstructions?.some(
    (b) => (b.primary.modifier ?? '').toLowerCase() === 'uturn',
  );
}

/** Left/right road turns that benefit from knowing whether the junction is under a bridge. */
export function isStructureTurnStep(step: RouteStep | null | undefined): step is RouteStep {
  if (!step) return false;
  const modifier = (step.maneuver.modifier ?? '').toLowerCase();
  if (
    modifier === 'uturn' ||
    step.bannerInstructions?.some(
      (b) => (b.primary.modifier ?? '').toLowerCase() === 'uturn',
    )
  ) {
    return true;
  }
  const type = (step.maneuver.type ?? '').toLowerCase();
  if (type !== 'turn' && type !== 'end of road') return false;
  return modifier === 'left' || modifier === 'right' || modifier === 'sharp left' || modifier === 'sharp right';
}

/** Text Mapbox/OSM already named explicitly wins over every geometric guess. */
export function explicitManeuverStructure(step: RouteStep): ManeuverStructure {
  const banners = (step.bannerInstructions ?? []).flatMap((b) => [
    b.primary.text,
    b.secondary?.text ?? '',
    b.sub?.text ?? '',
    ...b.primary.components.map((c) => c.text),
    ...(b.secondary?.components ?? []).map((c) => c.text),
    ...(b.sub?.components ?? []).map((c) => c.text),
  ]);
  const text = [
    step.name,
    step.ref ?? '',
    step.destinations ?? '',
    step.exits ?? '',
    step.maneuver.instruction ?? '',
    ...banners,
  ].join(' ');
  if (UNDER_WORDS.test(text)) return 'under-bridge';
  if (BRIDGE_WORDS.test(text)) return 'bridge';
  if (ROAD_WORDS.test(text)) return 'road';

  // intersections[0] is the maneuver junction. Later intersections belong to
  // the road after the turn and must not lend their tunnel/bridge to this one.
  const classes = step.intersections?.[0]?.classes ?? [];
  if (classes.some((c) => /^(?:tunnel|underpass|covered)$/i.test(c))) return 'under-bridge';
  if (classes.some((c) => /^(?:bridge|flyover|overpass)$/i.test(c))) return 'bridge';
  return 'unknown';
}

export function explicitUturnStructure(step: RouteStep): UturnStructure {
  return isUturnStep(step) ? explicitManeuverStructure(step) : 'unknown';
}

/** Current classification for any supported road turn. */
export function maneuverStructure(
  step: RouteStep | null | undefined,
): ManeuverStructure {
  if (!step || !isStructureTurnStep(step)) return 'unknown';
  const explicit = explicitManeuverStructure(step);
  return explicit !== 'unknown' ? explicit : (STRUCTURE.get(step) ?? 'unknown');
}

/** Current classification. Synchronous so both the sign and voice use one answer. */
export function uturnStructure(step: RouteStep | null | undefined): UturnStructure {
  if (!step || !isUturnStep(step)) return 'unknown';
  return maneuverStructure(step);
}

function nearPath(coords: LngLat[] | undefined, point: LngLat): LngLat[] {
  if (!coords?.length) return [];
  const near = coords.filter((p) => distance(p, point) <= 260);
  if (near.length >= 2) return near;
  // Sparse geometries may have only the maneuver point and a far endpoint.
  // Keeping the nearest two preserves the direction without inventing a chord
  // between the previous and next step.
  return [...coords]
    .sort((a, b) => distance(a, point) - distance(b, point))
    .slice(0, Math.min(2, coords.length));
}

function collectSites(routes: Route[]): ManeuverSite[] {
  const sites: ManeuverSite[] = [];
  const seen = new Set<RouteStep>();
  for (const route of routes) {
    for (const leg of route.legs) {
      for (let i = 0; i < leg.steps.length; i++) {
        const step = leg.steps[i];
        if (!isStructureTurnStep(step) || seen.has(step)) continue;
        seen.add(step);
        const point = step.maneuver.location;
        const exitPath = nearPath(step.geometry?.coordinates, point);
        const paths = [
          nearPath(leg.steps[i - 1]?.geometry?.coordinates, point),
          exitPath,
        ].filter((line) => line.length >= 2);
        sites.push({ step, point, paths, exitPath });
      }
    }
  }
  return sites;
}

function wayPoints(way: OverpassWay): LngLat[] {
  return (way.geometry ?? []).map((p) => [p.lon, p.lat]);
}

function pointToLine(point: LngLat, line: LngLat[]): number {
  if (!line.length) return Infinity;
  if (line.length === 1) return distance(point, line[0]);
  let best = Infinity;
  for (let i = 0; i < line.length - 1; i++) {
    best = Math.min(best, nearestPointOnSegment(point, line[i], line[i + 1]).distance);
  }
  return best;
}

/** Symmetric line distance; catches sparse route and sparse OSM geometries. */
function lineDistance(a: LngLat[], b: LngLat[]): number {
  let best = Infinity;
  for (const p of a) best = Math.min(best, pointToLine(p, b));
  for (const p of b) best = Math.min(best, pointToLine(p, a));
  return best;
}

/** True when two nearby polylines run together, rather than merely crossing. */
function linesAlign(a: LngLat[], b: LngLat[]): boolean {
  for (let ai = 0; ai < a.length - 1; ai++) {
    if (distance(a[ai], a[ai + 1]) < 4) continue;
    const aBearing = bearing(a[ai], a[ai + 1]);
    for (let bi = 0; bi < b.length - 1; bi++) {
      if (distance(b[bi], b[bi + 1]) < 4) continue;
      const delta = Math.abs(bearingDelta(aBearing, bearing(b[bi], b[bi + 1])));
      const axisDelta = Math.min(delta, 180 - delta);
      if (
        axisDelta <= 30 &&
        lineDistance([a[ai], a[ai + 1]], [b[bi], b[bi + 1]]) <= ON_ROUTE_M
      ) {
        return true;
      }
    }
  }
  return false;
}

function yes(value: string | undefined): boolean {
  return !!value && !['no', 'false', '0'].includes(value.toLowerCase());
}

function layer(tags: Record<string, string>): number {
  const n = Number.parseFloat(tags.layer ?? '0');
  return Number.isFinite(n) ? n : 0;
}

function isBridge(way: OverpassWay): boolean {
  const tags = way.tags ?? {};
  return yes(tags.bridge) || tags.man_made === 'bridge' || layer(tags) > 0;
}

function isUnder(way: OverpassWay): boolean {
  const tags = way.tags ?? {};
  return yes(tags.tunnel) || yes(tags.covered) || layer(tags) < 0;
}

/**
 * Pure geometry/tag classifier, exported so the three safety cases can be
 * exercised without depending on a public Overpass server.
 */
export function classifyManeuverWays(
  site: ManeuverSite,
  ways: OverpassWay[],
): ManeuverStructure {
  // Older focused U-turn fixtures predate exitPath. Their last path is the
  // outgoing arm, so retain that shape while newer callers name it directly.
  const exitPath = site.exitPath?.length ? site.exitPath : (site.paths.at(-1) ?? []);
  const nearby = ways
    .map((way) => ({ way, points: wayPoints(way) }))
    .filter(({ points }) => points.length >= 2 && pointToLine(site.point, points) <= LOOKUP_M);

  // The road leaving the turn itself follows the elevated way. Match only the
  // exit arm here: a flyover often follows the approach into a junction while
  // the driver turns right beneath it, which is precisely the case this pass
  // must label as under-bridge rather than on-bridge.
  if (
    nearby.some(
      ({ way, points }) =>
        isBridge(way) && linesAlign(exitPath, points),
    )
  ) {
    return 'bridge';
  }

  // A tunnel/covered/lower-layer turning way, or a separate bridge crossing
  // the U-turn point, means the route passes beneath the structure.
  if (
    nearby.some(
      ({ way, points }) =>
        isUnder(way) && site.paths.some((path) => linesAlign(path, points)),
    ) ||
    nearby.some(({ way, points }) => isBridge(way) && pointToLine(site.point, points) <= AT_JUNCTION_M)
  ) {
    return 'under-bridge';
  }

  // The lookup succeeded and found the surrounding road network, but neither
  // the turn nor its junction carries a structure: ordinary on-road U-turn.
  return nearby.length ? 'road' : 'unknown';
}

/** Kept for existing focused tests and callers that exercise U-turn geometry. */
export const classifyUturnWays = classifyManeuverWays;

async function fetchWays(sites: ManeuverSite[], signal?: AbortSignal): Promise<OverpassWay[]> {
  const clauses = sites
    .map(({ point }) => `way["highway"](around:${LOOKUP_M},${point[1].toFixed(6)},${point[0].toFixed(6)});`)
    .join('\n');
  const query = `[out:json][timeout:22];(\n${clauses}\n);out tags geom;`;
  let lastError: unknown;
  for (const url of OVERPASS_URLS) {
    try {
      const res = await fetch(url, {
        method: 'POST',
        body: new URLSearchParams({ data: query }),
        signal,
      });
      if (!res.ok) throw new Error(`Overpass ${res.status}`);
      const body = (await res.json()) as { elements?: OverpassWay[] };
      return (body.elements ?? []).filter((el) => el.type === 'way');
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') throw err;
      lastError = err;
    }
  }
  throw lastError instanceof Error ? lastError : new Error('Overpass unreachable');
}

/** Enrich U-turns and ordinary road turns without delaying route display. */
export async function scanManeuverStructures(routes: Route[], signal?: AbortSignal): Promise<void> {
  const sites = collectSites(routes).filter((site) => explicitManeuverStructure(site.step) === 'unknown');
  if (!sites.length) return;
  const ways = await fetchWays(sites, signal);
  for (const site of sites) STRUCTURE.set(site.step, classifyManeuverWays(site, ways));
}


/** Backwards-compatible name for older call sites outside this recovered source tree. */
export const scanUturnStructures = scanManeuverStructures;
